import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import type { OpsOrderDetail } from '@hedgehog/shared'
import { opsApi } from '@/components/ops/api'
import { EmailsTable } from '@/components/ops/order-sections'
import { ErrorState, LoadingState } from '@/components/app/states'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'

type OutboxEmail = OpsOrderDetail['emails'][number]

export const Route = createFileRoute('/ops/emails')({
  loader: () => opsApi<{ emails: OutboxEmail[] }>('/ops/emails').then((r) => r.emails),
  head: () => ({ meta: [{ title: 'Emails · Ops' }] }),
  component: EmailsPage,
  pendingComponent: () => <LoadingState label="Loading outbox" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the outbox" />,
})

function EmailsPage() {
  const emails = Route.useLoaderData()
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const rows = needle
    ? emails.filter((m) => [m.to, m.template, m.subject, m.status].some((v) => v.toLowerCase().includes(needle)))
    : emails
  const failed = emails.filter((m) => /fail|bounce|error/i.test(m.status)).length

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <h1 className="mr-auto text-base font-semibold">
          Outbox <span className="font-normal text-muted-foreground">({emails.length})</span>
          {failed > 0 && <span className="ml-2 text-sm font-normal text-destructive">{failed} failed</span>}
        </h1>
        <div className="flex flex-col gap-1">
          <Label htmlFor="emails-q" className="text-xs text-muted-foreground">
            Filter
          </Label>
          <Input
            id="emails-q"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Recipient, template, subject, status"
            className="w-72"
          />
        </div>
      </div>
      {rows.length === 0 ? (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>No emails</EmptyTitle>
            <EmptyDescription>{needle ? 'No email matches the filter.' : 'Sent and queued emails appear here, newest first.'}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="rounded-lg border border-border">
          <EmailsTable emails={rows} />
        </div>
      )}
    </div>
  )
}
